import {BackSide, CapsuleGeometry, CustomBlending, MaxEquation, Mesh, MeshLambertMaterial, MinEquation, OneFactor, ShaderMaterial, NearestFilter, TextureLoader} from 'three';
import carTextureUrl from './car-texture';
import busTextureUrl from './bus-texture';
import {blink} from '../helpers/helpers';
import AircraftGeometry from './aircraft-geometry.js';
import BusGeometry, {BUS_GLYPH_STRIP, BUS_MESH_HEIGHT, BUS_MESH_LENGTH, BUS_MESH_WIDTH, BUS_ROOF_BAND} from './bus-geometry.js';
import CarGeometry, {CAR_GAP_RATIO, CAR_MESH_HEIGHT, CAR_MESH_LENGTH, CAR_MESH_WIDTH} from './car-geometry.js';
import InstancedGeometry from './instanced-geometry.js';
import commonVertexShaderChunk from './common-vertex.glsl';
import colorVertexShaderChunk from './color-vertex.glsl';
import beginnormalVertexShaderChunk from './beginnormal-vertex.glsl';
import beginVertexShaderChunk from './begin-vertex.glsl';
import commonFragmentShaderChunk from './common-fragment.glsl';
import diffuseColorFragmentShaderChunk from './diffuse-color-fragment.glsl';
import outlineFragmentShader from './outline-fragment.glsl';
import outlineVertexShader from './outline-vertex.glsl';
import pickingFragmentShader from './picking-fragment.glsl';
import pickingVertexShader from './picking-vertex.glsl';
import delayMarkerFragmentShader from './delay-marker-fragment.glsl';
import delayMarkerVertexShader from './delay-marker-vertex.glsl';

// 캡슐 양 끝 반구를 진행방향으로 누르는 비율. 1 이면 정확한 반구고, 낮추면 곡률이
// 커지면서(더 급하게 꺾이면서) 총 길이가 줄어 앞뒤로 덜 튀어나온다.
const DELAY_MARKER_CAP_FLATTEN = .6;

// 지연 마커 캡슐의 굵기를 차량 폭에서 유도한다. 차량 폭보다 조금 넓어야 열차에 딱
// 붙는 띠로 읽힌다 (구체였을 때의 1.8 은 너무 굵어 열차를 덮는 후광이 됐다). 상수로
// 따로 적으면 차량 치수를 줄일 때 띠만 남아 상대적으로 두꺼워진다 — 칸 간격을
// CAR_MESH_LENGTH 에서 유도한 것과 같은 이유다.
const DELAY_MARKER_WIDTH_RATIO = 1.02;
const DELAY_MARKER_RADIUS = CAR_MESH_WIDTH * DELAY_MARKER_WIDTH_RATIO;

/**
 * Squashes a capsule's end caps along its axis, so they curve harder and stick out less.
 * 몸통(|y| <= halfBody)은 건드리지 않으므로 굵기와 이음매 겹침은 그대로다.
 * @param {BufferGeometry} geometry - CapsuleGeometry
 * @param {number} halfBody - 몸통 절반 길이
 * @param {number} factor - 누르는 비율 (1 이면 그대로)
 */
function flattenCapsuleCaps(geometry, halfBody, factor) {
    const position = geometry.getAttribute('position');

    for (let i = 0; i < position.count; i++) {
        const y = position.getY(i),
            body = Math.min(Math.max(y, -halfBody), halfBody);

        position.setY(i, body + (y - body) * factor);
    }
    position.needsUpdate = true;
    // 눌린 만큼 법선이 어긋나므로 다시 구한다 — 강도(vIntensity)가 법선을 읽는다.
    geometry.computeVertexNormals();
}

export default class {

    constructor(type, count, parameters) {
        const me = this;

        const baseGeometry =
            type === 'CAR' ? new CarGeometry(CAR_MESH_WIDTH, CAR_MESH_LENGTH, CAR_MESH_HEIGHT) :
            type === 'AIRCRAFT' ? new AircraftGeometry(.88, 2.64, .88, .1) :
            // 상류는 여기에 BoxGeometry(.6, 1.2, .6) 를 쓰는데 UV 가 없어 색 하나로만
            // 칠해진다. 면마다 아틀라스를 지정한 BusGeometry 로 바꿔 CAR 과 같은
            // 마젠타 마스크 텍스처를 입힌다.
            new BusGeometry(BUS_MESH_WIDTH, BUS_MESH_LENGTH, BUS_MESH_HEIGHT);
        // 바디가 rounded box 이므로 윤곽선도 같은 형상을 써야 어긋나지 않는다
        const baseOutlineGeometry =
            type === 'CAR' ? new CarGeometry(CAR_MESH_WIDTH, CAR_MESH_LENGTH, CAR_MESH_HEIGHT) : undefined;

        const uniforms = me.uniforms = {
            zoom: {value: parameters.zoom},
            cameraZ: {value: parameters.cameraZ},
            modelScale: {value: parameters.modelScale},
            textureData0: {value: null},
            textureData1: {value: null},
            textureColor: {value: null},
            textureBody: {value: null},
            // 지붕 노선번호용. BUS 프래그먼트만 읽는다 (다른 타입은 선언도 안 된다).
            roofBand: {value: BUS_ROOF_BAND},
            glyphStrip: {value: BUS_GLYPH_STRIP}
        };

        const bodyTextureUrl =
            type === 'CAR' ? carTextureUrl :
            type === 'BUS' ? busTextureUrl : undefined;

        if (bodyTextureUrl) {
            new TextureLoader().load(bodyTextureUrl, texture => {
                // 마스크(마젠타)를 색으로 판정하므로 보간/밉맵으로 섞이면 판정이 깨진다.
                // 축소될 때 마젠타가 흰색과 섞여 그대로 렌더되는 문제가 있었다.
                texture.magFilter = NearestFilter;
                texture.minFilter = NearestFilter;
                texture.generateMipmaps = false;
                uniforms.textureBody.value = texture;
            });
        }

        const geometry = me.geometry = new InstancedGeometry(baseGeometry, count);
        const material = me.material = new MeshLambertMaterial({
            transparent: true
        });

        material.onBeforeCompile = shader => {
            Object.assign(shader.uniforms, uniforms);
            shader.vertexShader = shader.vertexShader
                .replace('#include <common>', commonVertexShaderChunk)
                .replace('#include <color_vertex>', colorVertexShaderChunk)
                .replace('#include <beginnormal_vertex>', beginnormalVertexShaderChunk)
                .replace('#include <begin_vertex>', beginVertexShaderChunk);
            shader.fragmentShader = shader.fragmentShader
                .replace('#include <common>', commonFragmentShaderChunk)
                .replace('vec4 diffuseColor = vec4( diffuse, opacity );', diffuseColorFragmentShaderChunk);
            shader.defines = {[type]: true};
        };
        material.customProgramCacheKey = () => type;

        const mesh = me.mesh = new Mesh(geometry, material);

        mesh.updateMatrix();
        mesh.matrixAutoUpdate = false;
        mesh.frustumCulled = false;

        const pickingMaterial = me.pickingMaterial = new ShaderMaterial({
            uniforms,
            vertexShader: pickingVertexShader,
            fragmentShader: pickingFragmentShader,
            defines: {[type]: true}
        });

        const pickingMesh = me.pickingMesh = new Mesh(geometry, pickingMaterial);

        pickingMesh.updateMatrix();
        pickingMesh.matrixAutoUpdate = false;
        pickingMesh.frustumCulled = false;

        const outlineGeometry = me.outlineGeometry = new InstancedGeometry(baseOutlineGeometry || baseGeometry, 2);
        const outlineMaterial = me.outlineMaterial = new ShaderMaterial({
            uniforms: Object.assign({}, uniforms, {
                marked: {value: -1},
                tracked: {value: -1},
                intensity: {value: 0}
            }),
            vertexShader: outlineVertexShader,
            fragmentShader: outlineFragmentShader,
            defines: {[type]: true},
            transparent: true,
            side: BackSide
        });

        const outlineMesh = me.outlineMesh = new Mesh(outlineGeometry, outlineMaterial);

        outlineMesh.updateMatrix();
        outlineMesh.matrixAutoUpdate = false;
        outlineMesh.frustumCulled = false;

        baseGeometry.dispose();
        if (baseOutlineGeometry) {
            baseOutlineGeometry.dispose();
        }

        if (type === 'CAR') {
            // 칸마다 캡슐 하나. 칸을 따라 이어져 편성 전체가 끝이 둥근 원통 하나로
            // 보이고, 마커가 칸별이라 곡선에서도 열차를 따라 휜다. (편성 전체를 캡슐
            // 하나로 그리면 곡선에서 직선으로 뻗어 빈 땅을 덮는다 — 실측으로 확인했다.)
            //
            // 몸통을 칸 간격만큼 잡으면 양 끝 반구(반지름씩)가 그대로 이웃과의 겹침이
            // 된다. MIN 블렌딩은 멱등이라 겹쳐도 색이 진해지지 않으므로 겹침이 공짜고,
            // 넉넉히 겹쳐야 관절이 잘리지 않고 띠가 매끄럽게 이어진다.
            const bodyLength = CAR_MESH_LENGTH * CAR_GAP_RATIO,
                capsuleGeometry = new CapsuleGeometry(DELAY_MARKER_RADIUS, bodyLength, 12, 24);

            flattenCapsuleCaps(capsuleGeometry, bodyLength / 2, DELAY_MARKER_CAP_FLATTEN);
            const delayMarkerGeometry = me.delayMarkerGeometry = new InstancedGeometry(capsuleGeometry, count);
            const delayMarkerMaterial = me.delayMarkerMaterial = new ShaderMaterial({
                uniforms: Object.assign({}, uniforms, {
                    base: {value: 1}
                }),
                vertexShader: delayMarkerVertexShader,
                fragmentShader: delayMarkerFragmentShader,
                // 캡슐이 서로 겹치므로 누적되는 블렌딩을 쓰면 안 된다. 곱셈·덧셈은
                // 겹친 자리를 더 어둡게/밝게 만들어 이어진 하나가 아니라 원 여러 개로
                // 보이게 한다. MIN 은 멱등이라 몇 번을 겹쳐도 같은 색이다.
                blending: CustomBlending,
                blendEquation: MinEquation,
                blendSrc: OneFactor,
                blendDst: OneFactor,
                premultipliedAlpha: true,
                depthWrite: false
            });

            const delayMarkerMesh = me.delayMarkerMesh = new Mesh(delayMarkerGeometry, delayMarkerMaterial);

            delayMarkerMesh.updateMatrix();
            delayMarkerMesh.matrixAutoUpdate = false;
            delayMarkerMesh.frustumCulled = false;

            capsuleGeometry.dispose();
        }

    }

    getMesh() {
        return this.mesh;
    }

    getPickingMesh() {
        return this.pickingMesh;
    }

    getOutlineMesh() {
        return this.outlineMesh;
    }

    getDelayMarkerMesh() {
        return this.delayMarkerMesh;
    }

    refreshCameraParams(params) {
        const uniforms = this.uniforms;

        uniforms.zoom.value = params.zoom;
        uniforms.cameraZ.value = params.cameraZ;
    }

    setTextures(textures) {
        const me = this,
            uniforms = me.uniforms;

        uniforms.textureData0.value = textures[0];
        uniforms.textureData1.value = textures[1];
        uniforms.textureColor.value = textures[2];

        me.outlineMaterial.uniforms.intensity.value = blink();
    }

    setInstanceIDs(ids) {
        const me = this,
            delayMarkerGeometry = me.delayMarkerGeometry;

        me.geometry.setInstanceIDs(ids.body);
        me.outlineGeometry.setInstanceIDs(ids.outline);
        if (delayMarkerGeometry) {
            delayMarkerGeometry.setInstanceIDs(ids.delayMarker);
        }
    }

    setMarkedInstanceID(id) {
        this.outlineMaterial.uniforms.marked.value = id;
    }

    setTrackedInstanceID(id) {
        this.outlineMaterial.uniforms.tracked.value = id;
    }

    refreshDelayMarkerMesh(dark) {
        const {delayMarkerMaterial, delayMarkerMesh} = this;

        if (delayMarkerMaterial) {
            delayMarkerMaterial.uniforms.base.value = dark ? 0 : 1;
        }
        if (delayMarkerMesh) {
            // 어두운 배경에서는 밝히는 쪽이 보인다. MAX 도 MIN 과 같이 멱등이다.
            delayMarkerMesh.material.blendEquation = dark ? MaxEquation : MinEquation;
            delayMarkerMesh.material.needsUpdate = true;
        }
    }

    dispose() {
        const me = this,
            {delayMarkerGeometry, delayMarkerMaterial} = me;

        me.geometry.dispose();
        me.material.dispose();
        me.pickingMaterial.dispose();
        me.outlineGeometry.dispose();
        me.outlineMaterial.dispose();
        if (delayMarkerGeometry) {
            delayMarkerGeometry.dispose();
        }
        if (delayMarkerMaterial) {
            delayMarkerMaterial.dispose();
        }
    }

}
